/* gr-bundle.js — Bundle Builder behavior.
 *
 * State:
 *   { size, filled[], grind, planId, sizeMeta }
 *
 * Cart submission:
 *   POST /cart/add.js with:
 *     id: bundle parent variant id for the chosen size
 *     quantity: 1
 *     selling_plan: plan id or empty
 *     properties:
 *       _bundle_children: JSON string of [{id, title}, ...]
 *       _bundle_size: N
 *       _bundle_grind: 'whole' | 'ground'
 *
 *   A Shopify Cart Transform Function expands _bundle_children into child
 *   line items at checkout so inventory deducts per bag. See
 *   data/bundle-cart-transform.md for the Function source.
 */

(() => {
  const root = document.querySelector('.gr-bundle');
  if (!root) return;

  const state = {
    size: parseInt(root.dataset.defaultSize, 10) || 6,
    sizeMeta: null, // { variantId, price, save, label }
    filled: [],
    grind: 'whole',
    bagWeight: '340g',
    planId: '',
  };

  function parseVariants(card) {
    try { return JSON.parse(card.dataset.variants || '[]'); }
    catch (e) { return []; }
  }

  const ALL_WEIGHT_TOKENS = ['907', '2lb', '2 lb', '340', '12oz', '12 oz'];

  // A product has a weight dimension only if some variant option names a weight.
  // Grind-only products (e.g. Elixir Decaf, currently 12oz-only) have none — they
  // can't fulfil a 907g bundle, so callers flag/relabel them as the small size.
  function productHasWeightDimension(variants) {
    return Array.isArray(variants) && variants.some((v) =>
      (v.options || []).some((o) => ALL_WEIGHT_TOKENS.some((t) => String(o).toLowerCase().includes(t))));
  }

  // Resolve a product's variant from the chosen bag weight + grind.
  // Matches by token so option naming variations ("907g", "907 g (2lbs)",
  // "Ground", "Whole Bean", "Moulu", "Grain entier", etc.) all work in
  // both English and French stores. Returns the variant object or null.
  function resolveVariant(variants, bagWeight, grind) {
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const weightTokens = bagWeight === '907g'
      ? ['907', '2lb', '2 lb']
      : ['340', '12oz', '12 oz'];
    const grindTokens = grind === 'ground'
      ? ['ground', 'grind', 'moulu']
      : ['whole', 'entier', 'grain'];
    // Some products (e.g. decaf) only carry a grind option and no weight option.
    // Only require a weight match when the product actually has a weight dimension,
    // otherwise resolveVariant returns null and the caller falls back to the
    // default (whole-bean) variant — adding "whole bean" even when "ground" is chosen.
    const productHasWeight = productHasWeightDimension(variants);
    const matches = (v) => {
      const opts = (v.options || []).map((o) => String(o).toLowerCase());
      const hasGrind = opts.some((o) => grindTokens.some((t) => o.includes(t)));
      if (!productHasWeight) return hasGrind;
      const hasWeight = opts.some((o) => weightTokens.some((t) => o.includes(t)));
      return hasWeight && hasGrind;
    };
    return variants.find((v) => matches(v) && v.available !== false)
      || variants.find(matches)
      || null;
  }

  function resolveVariantId(variants, bagWeight, grind, defaultId) {
    const v = resolveVariant(variants, bagWeight, grind);
    return v ? v.id : defaultId;
  }

  const i18n = {
    bags: root.dataset.i18nBags || 'BAGS',
    savePrefix: root.dataset.i18nSavePrefix || 'SAVE',
    perBag: root.dataset.i18nPerBag || '/ BAG',
    removeAria: root.dataset.i18nRemoveAria || 'Remove',
    emptySlot: root.dataset.i18nEmptySlot || 'EMPTY SLOT',
    full: root.dataset.i18nFull || 'FULL',
    addToBundle: root.dataset.i18nAddToBundle || '+ ADD TO BUNDLE',
    addBundle: root.dataset.i18nAddBundle || 'ADD BUNDLE',
    adding: root.dataset.i18nAdding || 'ADDING…',
    cartError: root.dataset.i18nCartError || 'Something went wrong adding your bundle. Please try again.',
    fillPrefix: root.dataset.i18nFillPrefix || 'FILL',
    fillSuffixPlural: root.dataset.i18nFillSuffixPlural || 'MORE SLOTS',
    fillSuffixSingular: root.dataset.i18nFillSuffixSingular || 'MORE SLOT',
    bundleFallback: root.dataset.i18nBundleFallback || 'BUNDLE',
  };

  // Elements
  const $slots = root.querySelector('[data-slots]');
  const $filled = root.querySelectorAll('[data-filled]');
  const $sizeOut = root.querySelectorAll('[data-size-display]');
  const $summaryLabel = root.querySelector('[data-summary-label]');
  const $summarySize = root.querySelector('[data-summary-size]');
  const $total = root.querySelectorAll('[data-total]');
  const $alacarteRow = root.querySelector('[data-alacarte-row]');
  const $alacarte = root.querySelector('[data-alacarte]');
  const $bundleSave = root.querySelector('[data-bundle-save]');
  const $subRow = root.querySelector('[data-sub-row]');
  const $add = root.querySelectorAll('[data-bundle-add]');
  const $addLabel = root.querySelectorAll('[data-add-label]');
  const $stepper = root.querySelectorAll('[data-step-index]');

  // Display label for the small bag size (e.g. "340G (12OZ)"), read from the chip
  // so it stays localized. Used to relabel 12oz-only products in a 907g bundle.
  const smallWeightChip = root.querySelector('[data-bag-weight="340g"]');
  const SMALL_SIZE_LABEL = smallWeightChip ? smallWeightChip.textContent.trim() : '12 oz';

  // Customer-facing title for a filled product. When a 12oz-only product is added
  // to a 907g bundle, annotate it with the small size so the line item is honest.
  function displayTitle(f) {
    if (state.bagWeight === '907g' && !productHasWeightDimension(f.variants)) {
      return f.title + ' · ' + SMALL_SIZE_LABEL;
    }
    return f.title;
  }

  const setText = (els, val) => els.forEach((el) => { el.textContent = val; });
  const setDisabled = (els, val) => els.forEach((el) => { el.disabled = val; });

  // ── Size buttons ──────────────────────────────────────────────
  const sizeLabels = {
    '3': root.dataset.i18nStarterLabel || 'STARTER',
    '6': root.dataset.i18nRiderLabel || 'RIDER',
  };
  const sizeButtons = root.querySelectorAll('.gr-bundle__size');
  sizeButtons.forEach((btn) => {
    // Fix: Shopify sometimes strips the first size card's inner HTML
    if (!btn.querySelector('.gr-bundle__size-lbl')) {
      const s = btn.dataset;
      const frag = document.createDocumentFragment();
      const mkDiv = (cls, txt) => { const d = document.createElement('div'); d.className = cls; d.textContent = txt; return d; };
      frag.appendChild(mkDiv('gr-bundle__size-lbl', sizeLabels[s.size] || i18n.bundleFallback));
      frag.appendChild(mkDiv('gr-bundle__size-n', s.size));
      frag.appendChild(mkDiv('gr-bundle__size-bags', i18n.bags));
      frag.appendChild(mkDiv('gr-bundle__size-save', i18n.savePrefix + ' ' + s.save + '%'));
      btn.textContent = '';
      btn.appendChild(frag);
    }
    const selectSize = () => {
      sizeButtons.forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-checked', 'true');
      state.size = parseInt(btn.dataset.size, 10);
      state.sizeMeta = {
        variantId: btn.dataset.variantId,
        price: parseFloat(btn.dataset.price),
        save: parseInt(btn.dataset.save, 10),
        label: btn.querySelector('.gr-bundle__size-lbl').textContent,
      };
      state.filled = state.filled.slice(0, state.size);
      render();
    };
    btn.addEventListener('click', selectSize);
    // Keyboard support for radio role: Space/Enter activates, ArrowLeft/Right move between
    btn.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        selectSize();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const buttons = Array.from(sizeButtons);
        const idx = buttons.indexOf(btn);
        const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        const next = buttons[(idx + dir + buttons.length) % buttons.length];
        next.focus();
        next.click();
      }
    });
  });
  // Select default
  const defaultBtn = Array.from(sizeButtons).find((b) => parseInt(b.dataset.size, 10) === state.size) || sizeButtons[0];
  if (defaultBtn) defaultBtn.click();

  // ── Bag weight ────────────────────────────────────────────────
  root.querySelectorAll('[data-bag-weight]').forEach((chip) => {
    chip.addEventListener('click', () => {
      root.querySelectorAll('[data-bag-weight]').forEach((c) => {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
      state.bagWeight = chip.dataset.bagWeight;
      render();
    });
  });

  // ── Grind ─────────────────────────────────────────────────────
  root.querySelectorAll('[data-grind]').forEach((chip) => {
    chip.addEventListener('click', () => {
      root.querySelectorAll('[data-grind]').forEach((c) => {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
      state.grind = chip.dataset.grind;
      render();
    });
  });

  // ── Frequency / selling plan ─────────────────────────────────
  root.querySelectorAll('[data-freq]').forEach((row) => {
    row.addEventListener('click', () => {
      root.querySelectorAll('[data-freq]').forEach((r) => r.classList.remove('is-active'));
      row.classList.add('is-active');
      state.planId = row.dataset.plan || '';
      render();
    });
  });

  // ── Picker cards → fill slot ─────────────────────────────────
  root.querySelectorAll('.gr-bundle__picker-card').forEach((card) => {
    const btn = card.querySelector('[data-add-to-bundle]');
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (state.filled.length >= state.size) return;
      const img = card.querySelector('img');
      state.filled.push({
        id: card.dataset.productId,
        handle: card.dataset.productHandle,
        title: card.dataset.productTitle,
        image: img ? img.src : '',
        sellingPlans: card.dataset.sellingPlans || '',
        variants: parseVariants(card),
      });
      render();
    });
  });

  // ── Render ────────────────────────────────────────────────────
  function render() {
    const n = state.size;
    const filled = state.filled.length;

    setText($sizeOut, n);
    $summarySize.textContent = n;
    $summaryLabel.textContent = state.sizeMeta?.label || '';
    setText($filled, filled);

    // Slots
    $slots.innerHTML = '';
    for (let i = 0; i < n; i++) {
      const slot = state.filled[i];
      const el = document.createElement('div');
      el.className = 'gr-bundle__slot' + (slot ? ' is-filled' : '');
      if (slot) {
        if (slot.image) {
          const img = document.createElement('img');
          img.src = slot.image;
          img.alt = '';
          img.className = 'gr-bundle__slot-img';
          img.loading = 'lazy';
          el.appendChild(img);
        }
        const nameDiv = document.createElement('div');
        nameDiv.className = 'gr-bundle__slot-name';
        nameDiv.textContent = String(slot.title || '').toUpperCase();
        el.appendChild(nameDiv);
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'gr-bundle__slot-x';
        removeBtn.setAttribute('aria-label', i18n.removeAria);
        removeBtn.textContent = '\u00d7';
        removeBtn.addEventListener('click', () => {
          state.filled.splice(i, 1);
          render();
        });
        el.appendChild(removeBtn);
      } else {
        const numDiv = document.createElement('div');
        numDiv.className = 'gr-bundle__slot-num';
        numDiv.textContent = String(i + 1).padStart(2, '0');
        el.appendChild(numDiv);
        const emptyDiv = document.createElement('div');
        emptyDiv.className = 'gr-bundle__slot-empty';
        emptyDiv.textContent = i18n.emptySlot;
        el.appendChild(emptyDiv);
      }
      $slots.appendChild(el);
    }

    // Picker availability
    const full = filled >= n;
    root.querySelectorAll('[data-add-to-bundle]').forEach((b) => {
      b.disabled = full;
      b.textContent = full ? i18n.full : i18n.addToBundle;
    });

    // 12oz-only products (no weight dimension) can't fulfil a 907g bundle — flag
    // them when the large size is selected so the shopper isn't surprised.
    root.querySelectorAll('.gr-bundle__picker-card').forEach((card) => {
      const flag = card.querySelector('[data-weight-flag]');
      if (!flag) return;
      const weightless = !productHasWeightDimension(parseVariants(card));
      flag.hidden = !(weightless && state.bagWeight === '907g');
    });

    // Totals — compute from resolved variant prices so 907g/Ground reflect reality.
    let subtotalCents = 0;
    let compareAtCents = 0;
    let resolvedCount = 0;
    state.filled.forEach((f) => {
      const v = resolveVariant(f.variants, state.bagWeight, state.grind);
      if (v) {
        subtotalCents += Number(v.price) || 0;
        compareAtCents += Number(v.compareAt || v.price) || 0;
        resolvedCount += 1;
      }
    });
    const bundlePrice = resolvedCount > 0 ? subtotalCents / 100 : 0;
    const finalPrice = state.planId ? bundlePrice * 0.85 : bundlePrice;
    setText($total, '$' + finalPrice.toFixed(2));
    $bundleSave.textContent = '−' + (state.sizeMeta?.save || 0) + '%';
    $subRow.hidden = !state.planId;
    // A la carte: only show when compare_at sum is meaningfully higher.
    const alacarte = compareAtCents / 100;
    if (resolvedCount > 0 && alacarte > bundlePrice + 0.01) {
      $alacarteRow.hidden = false;
      $alacarte.textContent = '$' + alacarte.toFixed(2);
    } else {
      $alacarteRow.hidden = true;
    }

    // CTA state
    const missing = n - filled;
    if (missing > 0) {
      setDisabled($add, true);
      const fillSuffix = missing !== 1 ? i18n.fillSuffixPlural : i18n.fillSuffixSingular;
      setText($addLabel, `${i18n.fillPrefix} ${missing} ${fillSuffix}`);
    } else {
      setDisabled($add, false);
      setText($addLabel, `${i18n.addBundle} · $${finalPrice.toFixed(2)}`);
    }

    // Stepper
    $stepper.forEach((s) => s.classList.remove('is-active'));
    $stepper[0]?.classList.add('is-active');
    if (filled > 0) $stepper[1]?.classList.add('is-active');
    if (filled === n) $stepper[2]?.classList.add('is-active');
  }

  // ── Add to cart ──────────────────────────────────────────────
  const onAddClick = async () => {
    if ($add[0].disabled) return;
    setDisabled($add, true);
    setText($addLabel, i18n.adding);

    let payload;

    if (state.sizeMeta?.variantId) {
      // Bundle parent product exists — add as single bundle line item
      payload = {
        items: [
          {
            id: parseInt(state.sizeMeta.variantId, 10),
            quantity: 1,
            selling_plan: state.planId || undefined,
            properties: {
              _bundle_children: JSON.stringify(state.filled.map((f) => ({
                id: resolveVariantId(f.variants, state.bagWeight, state.grind, parseInt(f.id, 10)),
                title: displayTitle(f),
              }))),
              _bundle_size: String(state.size),
              _bundle_grind: state.grind,
              _bundle_bag_weight: state.bagWeight || '340g',
              Items: state.filled.map((f) => displayTitle(f)).join(' \u00b7 '),
            },
          },
        ],
      };
    } else {
      // No bundle parent — add each coffee individually with its own selling plan
      // Parse the selected frequency name to find matching plan per product
      var selectedFreqBtn = root.querySelector('[data-freq].is-active');
      var selectedFreqName = selectedFreqBtn ? selectedFreqBtn.querySelector('.gr-bundle__freq-l')?.textContent?.trim()?.toLowerCase() : '';

      payload = {
        items: state.filled.map((f) => {
          var item = {
            id: resolveVariantId(f.variants, state.bagWeight, state.grind, parseInt(f.id, 10)),
            quantity: 1,
            properties: {
              _bundle_label: 'Bundle \u00b7 ' + state.size + '-pack',
              Grind: state.grind === 'whole' ? 'Whole Bean' : 'Ground',
              // 12oz-only products are always the small size, whatever the bundle weight.
              'Bag Size': productHasWeightDimension(f.variants) ? (state.bagWeight || '340g') : '340g',
            },
          };
          // Match selling plan by frequency name
          if (state.planId && f.sellingPlans) {
            var plans = f.sellingPlans.split(',');
            for (var p = 0; p < plans.length; p++) {
              var parts = plans[p].trim().split(':');
              if (parts.length === 2 && selectedFreqName.includes(parts[0].trim())) {
                item.selling_plan = parseInt(parts[1].trim(), 10);
                break;
              }
            }
          }
          return item;
        }),
      };
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      // Try to open cart drawer
      window.dispatchEvent(new CustomEvent('gr:cart:open'));
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.open) cartDrawer.open();
    } catch (err) {
      console.error(err);
      alert(i18n.cartError);
    } finally {
      render();
    }
  };
  $add.forEach((btn) => btn.addEventListener('click', onAddClick));

  render();
})();
// v4
